<template>
  <div>
    <v-container fill-height fluid>
      <v-row>
        <v-col class="pa-0" xs="12" md="12">
          <v-card outlined class="mx-auto px-2">
            <v-row>
              <v-col md="6">
                <div class="px-2 primary--text font-weight-bold">
                  {{ $vuetify.lang.t("$vuetify.allowed_ips.title") }}
                </div>
              </v-col>
              <v-col md="6">
                <div class="float-right">
                  <Button
                    :buttonType="FLOATING_ACTION_BUTTON"
                    iconName="mdi-plus"
                    size="x-small"
                    :buttonText="$vuetify.lang.t('$vuetify.common.create')"
                    color="primary"
                    @onClick="openCreateDialog"
                    :disabled="isLoading"
                  />
                </div>
              </v-col>
            </v-row>
            <v-divider></v-divider>

            <!-- Search Bar -->
            <v-row class="px-2 pt-3">
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="search"
                  prepend-inner-icon="mdi-magnify"
                  :label="$vuetify.lang.t('$vuetify.common.search_btn_txt')"
                  clearable
                  outlined
                  dense
                  hide-details
                ></v-text-field>
              </v-col>
            </v-row>

            <Loader v-if="isLoading" class="mr-2 mt-6" />
            <v-data-table
              v-else
              :headers="tableHeaders"
              :items="paginatedAllowedIps"
              item-key="id"
              :sort-by="['created_at']"
              :sort-desc="[true]"
              :items-per-page="itemsPerPage"
              hide-default-footer
            >
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="item.status === 'enabled' ? 'success' : 'error'"
                  text-color="white"
                  small
                >
                  {{ item.status }}
                </v-chip>
              </template>

              <template v-slot:item.allowed_type="{ item }">
                {{item.allowed_type === 'registration' ? 'User Registration' : item.allowed_type }}
              </template>

              <template v-slot:item.created_at="{ item }">
                {{item.created_at ? formatDateTime(item.created_at) : "N/A" }}
              </template>

              <template v-slot:item.updated_at="{ item }">
                {{item.updated_at ? formatDateTime(item.updated_at) : "N/A" }}
              </template>

              <template v-slot:item.actions="{ item }">
                <ButtonGroup
                  :detailsButton="false"
                  :editButton="true"
                  :deleteButton="true"
                  :addButton="false"
                  :startButton="item.status === 'disabled'"
                  :stopButton="item.status === 'enabled'"
                  :disabledEditButton="isLoad"
                  :disabledDeleteButton="isLoad"
                  :disabledStartButton="isLoad"
                  :disabledStopButton="isLoad"
                  @onEditClick="editAllowedIP(item)"
                  @onDeleteClick="deleteAllowedIP(item)"
                  @onStartClick="enableAllowedIP(item)"
                  @onStopClick="disableAllowedIP(item)"
                />
              </template>

              <template v-slot:no-data>
                <div class="empty-state-container">
                  <v-icon large class="empty-icon">mdi-ip</v-icon>
                  <p class="empty-label">
                    {{ search
                      ? $vuetify.lang.t('$vuetify.allowed_ips.no_results_found')
                      : $vuetify.lang.t('$vuetify.allowed_ips.no_ips_found')
                    }}
                  </p>
                </div>
              </template>
            </v-data-table>

            <v-row>
              <v-col class="py-0" cols="12" md="12">
                <v-divider></v-divider>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3"></v-col>
              <v-col cols="12" xs="12" sm="6" md="6">
                <v-pagination
                  class="text-center"
                  v-model="page"
                  :length="pageCount"
                ></v-pagination>
              </v-col>
              <v-col cols="12" offset-sm="1" offset-md="1" xs="3" sm="2" md="2">
                <v-select
                  v-model="itemsPerPage"
                  :items="rows"
                  hide-details="auto"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="primary--text font-weight-bold">
          {{
            isEditMode
              ? $vuetify.lang.t("$vuetify.allowed_ips.edit_ip")
              : $vuetify.lang.t("$vuetify.allowed_ips.add_new_ip")
          }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4">
          <v-form ref="ipForm">
            <v-text-field
              :label="$vuetify.lang.t('$vuetify.allowed_ips.ip_address')"
              v-model="formData.ip_address"
              placeholder="192.168.1.1 or 2001:0db8:85a3::8a2e:0370:7334"
              outlined
              :rules="[
                required($vuetify.lang.t('$vuetify.allowed_ips.ip_address')),
                ipAddress($vuetify.lang.t('$vuetify.allowed_ips.invalid_ip')),
              ]"
            ></v-text-field>

            <v-text-field
              :label="$vuetify.lang.t('$vuetify.allowed_ips.title_label')"
              v-model="formData.title"
              :placeholder="$vuetify.lang.t('$vuetify.allowed_ips.allowed_ip_placeholder')"
              outlined
              :rules="[required($vuetify.lang.t('$vuetify.allowed_ips.title_label'))]"
              class="mt-4"
            ></v-text-field>

            <v-select
              :label="$vuetify.lang.t('$vuetify.allowed_ips.status')"
              v-model="formData.status"
              :items="statusOptions"
              outlined
              class="mt-4"
              :rules="[required($vuetify.lang.t('$vuetify.allowed_ips.status'))]"
            ></v-select>

            <v-select
              :label="$vuetify.lang.t('$vuetify.allowed_ips.allowed_type')"
              v-model="formData.allowed_type"
              :items="allowedTypeOptions"
              outlined
              class="mt-4"
              :disabled="isEditMode"
              :rules="[required($vuetify.lang.t('$vuetify.allowed_ips.allowed_type'))]"
            ></v-select>
          </v-form>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="justify-end pa-4">
          <v-btn
            color="error"
            outlined
            @click="closeDialog"
          >
            {{ $vuetify.lang.t('$vuetify.common.cancel_btn') }}
          </v-btn>
          <v-btn
            color="primary"
            outlined
            @click="submitForm"
            :loading="isLoad"
          >
            {{ isEditMode
              ? $vuetify.lang.t('$vuetify.common.update_btn')
              : $vuetify.lang.t('$vuetify.common.create')
            }}
            <v-progress-circular v-if="isLoad" indeterminate color="white"></v-progress-circular>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <DialogModal
      v-model="deleteDialog"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="`${$vuetify.lang.t('$vuetify.allowed_ips.delete_confirmation_msg')}`"
      :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      :confirmBtnLoading="isLoad"
      @onConfirmation="confirmDelete"
      @onCancel="deleteDialog = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { formatDateTime } from "@/helpers/helper";
import {
  GET_ALL_ALLOWED_IPS,
  CREATE_ALLOWED_IP,
  UPDATE_ALLOWED_IP,
  DELETE_ALLOWED_IP,
  ADD_ALERT,
} from "@/store/_actiontypes";
import {
  TEXT_BUTTON,
  RAISED_BUTTON,
  FLOATING_ACTION_BUTTON,
} from "@/components/common/button/_buttontypes";
import validations from "@/helpers/validations";
import Loader from "@/components/common/loader/Loader";
import Button from "@/components/common/button/Button";
import DialogModal from "@/components/common/DialogModal";
import ButtonGroup from "@/components/common/buttongroup/ButtonGroup";

export default {
  name: "AdminAllowedIPs",
  components: {
    Loader,
    Button,
    DialogModal,
    ButtonGroup,
  },
  data() {
    return {
      search: "",
      dialog: false,
      deleteDialog: false,
      isEditMode: false,
      isLoading: false,
      isLoad: false,
      formatDateTime,
      selectedItem: null,
      formData: {
        ip_address: "",
        title: "",
        status: "enabled",
        allowed_type: "registration",
      },
      statusOptions: [
        { text: "Enabled", value: "enabled" },
        { text: "Disabled", value: "disabled" },
      ],
      allowedTypeOptions: [
        { text: "User Registration", value: "registration" }
      ],
      page: 1,
      itemsPerPage: 10,
      rows: [
        { text: "5", value: 5 },
        { text: "10", value: 10 },
        { text: "20", value: 20 },
      ],
      TEXT_BUTTON,
      RAISED_BUTTON,
      FLOATING_ACTION_BUTTON,
      ...validations,
    };
  },
  computed: {
    ...mapState({
      allAllowedIps: (state) => state.allowed_ips.allAllowedIps,
    }),
    tableHeaders() {
      return [
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.ip_address"), align: "start", sortable: false, value: "ip_address" },
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.title_label"), value: "title" },
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.status"), value: "status", sortable: false, },
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_type"), value: "allowed_type", sortable: false },
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.created_at"), sortable: true, value: "created_at" },
        { text: this.$vuetify.lang.t("$vuetify.allowed_ips.updated_at"), sortable: true, value: "updated_at" },
        { text: this.$vuetify.lang.t("$vuetify.common.actions"), value: "actions", sortable: false, align: "center" },
      ];
    },
    filteredAllowedIps() {
      if (!this.allAllowedIps?.length) return [];
      const query = (this.search || "").trim().toLowerCase();
      if (!query) return this.allAllowedIps;
      return this.allAllowedIps.filter((ip) =>
        ["ip_address", "title", "status", "allowed_type"].some((key) =>
          String(ip[key] ?? "").toLowerCase().includes(query)
        )
      );
    },
    pageCount() {
      const count = Math.ceil(this.filteredAllowedIps.length / (this.itemsPerPage || 1));
      return count > 0 ? count : 1;
    },
    paginatedAllowedIps() {
      const start = (this.page - 1) * this.itemsPerPage;
      return this.filteredAllowedIps.slice(start, start + this.itemsPerPage);
    },
  },
  mounted() {
    this.loadAllowedIPs();
  },
  watch: {
    search() { this.page = 1; },
    itemsPerPage() { this.page = 1; },
  },
  methods: {
    ...mapActions("allowed_ips", [
      GET_ALL_ALLOWED_IPS,
      CREATE_ALLOWED_IP,
      UPDATE_ALLOWED_IP,
      DELETE_ALLOWED_IP,
    ]),
    ...mapActions("alert", [ADD_ALERT]),

    async loadAllowedIPs() {
      this.isLoading = true;
      try {
        await this[GET_ALL_ALLOWED_IPS]();
      } catch (err) {
        console.error("Error loading allowed IPs:", err);
      } finally {
        this.isLoading = false;
      }
    },

    async enableAllowedIP(item) {
      this.isLoad = true;
      this.isLoading = true;
      try {
        await this[UPDATE_ALLOWED_IP]({
          id: item.id,
          payload: { ip_address: item.ip_address, title: item.title, status: "enabled", allowed_type: item.allowed_type },
        });
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_enabled_success_msg_txt"),
            color: "success",
          },
          { root: true }
        );
        await this.loadAllowedIPs();
      } catch (err) {
        console.error("Error enabling allowed IP:", err);
        const errorMessage =
          err?.response?.data?.message ||
          this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_error_msg_txt");
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: errorMessage, color: "error" },
          { root: true }
        );
      } finally {
        this.isLoad = false;
        this.isLoading = false;
      }
    },

    async disableAllowedIP(item) {
      this.isLoad = true;
      this.isLoading = true;
      try {
        await this[UPDATE_ALLOWED_IP]({
          id: item.id,
          payload: { ip_address: item.ip_address, title: item.title, status: "disabled", allowed_type: item.allowed_type },
        });
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_disabled_success_msg_txt"),
            color: "success",
          },
          { root: true }
        );
        await this.loadAllowedIPs();
      } catch (err) {
        console.error("Error disabling allowed IP:", err);
        const errorMessage =
          err?.response?.data?.message ||
          this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_error_msg_txt");
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: errorMessage, color: "error" },
          { root: true }
        );
      } finally {
        this.isLoad = false;
        this.isLoading = false;
      }
    },

    openCreateDialog() {
      this.isEditMode = false;
      this.resetForm();
      this.dialog = true;
    },

    editAllowedIP(item) {
      this.isEditMode = true;
      this.formData = {
        ip_address: item.ip_address,
        title: item.title,
        status: item.status,
        allowed_type: item.allowed_type,
      };
      this.selectedItem = item;
      this.dialog = true;
    },

    deleteAllowedIP(item) {
      this.selectedItem = item;
      this.deleteDialog = true;
    },

    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.formData = { ip_address: "", title: "", status: "enabled", allowed_type: "registration" };
      if (this.$refs.ipForm) this.$refs.ipForm.resetValidation();
    },

    async submitForm() {
      if (!this.$refs.ipForm.validate()) return;

      this.isLoad = true;
      try {
        if (this.isEditMode) {
          await this[UPDATE_ALLOWED_IP]({
            id: this.selectedItem.id,
            payload: { ...this.formData },
          });
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_update_success_msg_txt"),
              color: "success",
            },
            { root: true }
          );
        } else {
          await this[CREATE_ALLOWED_IP]({ ...this.formData });
          this.$store.dispatch(
            `alert/${ADD_ALERT}`,
            {
              message: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_create_success_msg_txt"),
              color: "success",
            },
            { root: true }
          );
        }
        this.closeDialog();
        await this.loadAllowedIPs();
      } catch (err) {
        console.error("Error saving allowed IP:", err);

        const errorMessage =
          err?.response?.data?.details?.ip_address?.[0] ||
          err?.response?.data?.message ||
          this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_error_msg_txt");

        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: errorMessage,
            color: "error",
          },
          { root: true }
        );
      } finally {
        this.isLoad = false;
      }
    },

    async confirmDelete() {
      this.isLoad = true;
      try {
        await this[DELETE_ALLOWED_IP]({ id: this.selectedItem.id });
        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          {
            message: this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_delete_success_msg_txt"),
            color: "success",
          },
          { root: true }
        );
        this.deleteDialog = false;
        this.selectedItem = null;
        await this.loadAllowedIPs();
        if (this.page > this.pageCount) this.page = this.pageCount;
      } catch (err) {
        console.error("Error deleting allowed IP:", err);
        const errorMessage =
          err?.response?.data?.message ||
          this.$vuetify.lang.t("$vuetify.allowed_ips.allowed_ip_error_msg_txt");

        this.$store.dispatch(
          `alert/${ADD_ALERT}`,
          { message: errorMessage, color: "error" },
          { root: true }
        );
      } finally {
        this.isLoad = false;
      }
    },
  },
};
</script>

<style scoped>
.elevation-1 {
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
}
.empty-state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
}
.empty-icon { color: #ccc; margin-bottom: 16px; }
.empty-label { margin: 0; font-size: 16px; color: #666; }
</style>